import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "nc4y_cookie_consent_v1";

/**
 * Cookie consent banner.
 *
 * Behaviour: shows once per browser window/tab session.
 * - Uses sessionStorage so closing the tab/window resets consent and the
 *   banner reappears next time the visitor opens a fresh window.
 * - Decisions persist while the visitor browses the site in that session.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const decision = sessionStorage.getItem(STORAGE_KEY);
      if (!decision) {
        // Slight delay so the banner doesn't compete with hero load
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
    } catch (e) {
      setVisible(true);
    }
  }, []);

  const decide = (value) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // Silent: storage may be unavailable in private mode
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-3 left-3 right-3 md:left-1/2 md:right-auto md:bottom-5 md:-translate-x-1/2 md:max-w-2xl z-[60] bg-white border border-[#E2E8F0] shadow-2xl"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="absolute top-0 left-0 w-full h-[3px] bg-[#C9A84C]" />
      <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 border border-[#C9A84C] flex items-center justify-center text-[#C9A84C] flex-shrink-0">
            <Cookie size={16} strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-base text-[#1E2761] tracking-tight">
              We use cookies
            </div>
            <p className="mt-1 text-xs md:text-sm text-[#334155]/80 leading-relaxed">
              Functional and anonymous analytics cookies to improve your
              experience. Read our{" "}
              <Link
                to="/cookie-policy"
                data-testid="cookie-banner-policy-link"
                className="text-[#1E2761] underline decoration-[#C9A84C] underline-offset-2"
              >
                Cookie Policy
              </Link>
              .
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => decide("declined")}
            data-testid="cookie-banner-decline"
            className="text-xs md:text-sm border border-[#1E2761] text-[#1E2761] px-4 py-2 font-medium tracking-wide hover:bg-[#F4F7FC] transition-colors whitespace-nowrap"
          >
            Decline
          </button>
          <button
            onClick={() => decide("accepted")}
            data-testid="cookie-banner-accept"
            className="text-xs md:text-sm bg-[#1E2761] text-white px-4 py-2 font-medium tracking-wide hover:bg-[#161d4b] transition-colors whitespace-nowrap"
          >
            Accept All
          </button>
          <button
            onClick={() => decide("dismissed")}
            data-testid="cookie-banner-close"
            className="text-[#334155]/50 hover:text-[#1E2761] flex-shrink-0 p-1"
            aria-label="Close cookie banner"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
